import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Search, User, X } from 'lucide-react';
import { customersService } from '../../services/customers.service';
import { Input } from '../ui';

const SEARCH_DEBOUNCE_MS = 300;

export const avatarInitials = (firstName, lastName, email) => {
  if (firstName && lastName) return `${firstName[0]}${lastName[0]}`.toUpperCase();
  if (firstName) return firstName.slice(0, 2).toUpperCase();
  if (email) return email.slice(0, 2).toUpperCase();
  return '??';
};

// ── Customer picker ────────────────────────────────────────────────────────
// Searchable combobox that resolves a real `customerId` from name/email/
// reference, with a small manual-ID escape hatch for when the directory is
// unavailable or the operator already knows the ID.
const CustomerPicker = ({
  value,
  onChange,
  placeholder = 'Search by name, email, or reference…',
  autoFocus,
  initialSelected = null,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [selected, setSelected] = useState(initialSelected);
  const [manualEntry, setManualEntry] = useState(false);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // initialSelected may resolve asynchronously in the parent (e.g. fetching
  // a customer by ID from a deep link), so sync it in when it arrives rather
  // than only reading it once at mount.
  useEffect(() => {
    if (initialSelected) setSelected(initialSelected);
  }, [initialSelected]);

  useEffect(() => {
    if (selected) return undefined;
    const trimmed = query.trim();
    if (!trimmed) {
      setResults([]);
      setLoadError(false);
      setOpen(false);
      return undefined;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await customersService.listCustomers({ search: trimmed });
        const list = Array.isArray(data?.users) ? data.users : Array.isArray(data) ? data : [];
        setResults(list);
        setLoadError(false);
        setOpen(true);
      } catch {
        setResults([]);
        setLoadError(true);
        setOpen(true);
      } finally {
        setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [query, selected]);

  const handleSelect = (customer) => {
    setSelected(customer);
    setQuery('');
    setResults([]);
    setOpen(false);
    onChange(customer.customerId, customer);
  };

  const handleClearSelection = () => {
    setSelected(null);
    setQuery('');
    onChange('', null);
  };

  const handleManualChange = (e) => {
    onChange(e.target.value, null);
  };

  const exitManualEntry = () => {
    setManualEntry(false);
    onChange('', null);
  };

  if (selected) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-lg border border-ink-200 bg-ink-50 px-3 py-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="h-7 w-7 rounded-full bg-white border border-ink-200 flex items-center justify-center text-2xs font-semibold text-ink-600 shrink-0">
            {avatarInitials(selected.firstName, selected.lastName, selected.email)}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-ink-900 truncate">
              {selected.firstName} {selected.lastName}
            </p>
            <p className="text-xs text-ink-500 truncate">{selected.email}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleClearSelection}
          className="shrink-0 text-ink-400 hover:text-ink-700"
          aria-label="Change customer"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  if (manualEntry) {
    return (
      <div className="space-y-1.5">
        <Input mono autoFocus value={value} onChange={handleManualChange} placeholder="cust-…" required />
        <button type="button" onClick={exitManualEntry} className="text-xs text-ink-500 hover:text-ink-700">
          Search customers instead
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
        <Input
          className="pl-9"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => { if (results.length > 0 || loadError) setOpen(true); }}
          placeholder={placeholder}
          autoFocus={autoFocus}
        />
        {query && (
          <button
            type="button"
            onClick={() => { setQuery(''); setResults([]); setOpen(false); }}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700"
            aria-label="Clear search"
          >
            <X className="h-4 w-4" />
          </button>
        )}

        {open && (
          <div className="absolute top-full mt-1 left-0 right-0 bg-white rounded-lg shadow-popover border border-ink-200 py-1 z-30 max-h-64 overflow-y-auto">
            {loading ? (
              <div className="flex items-center gap-2 px-3 py-3 text-sm text-ink-500">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Searching…
              </div>
            ) : loadError ? (
              <div className="px-3 py-3 text-sm text-danger-600">Couldn't load customers. Try again or enter the ID manually below.</div>
            ) : results.length === 0 ? (
              <div className="px-3 py-3 text-sm text-ink-500">No customers match "{query.trim()}".</div>
            ) : (
              results.map((c) => (
                <button
                  key={c.customerId}
                  type="button"
                  onClick={() => handleSelect(c)}
                  className="w-full px-3 py-2 text-left hover:bg-ink-50 flex items-center gap-2.5"
                >
                  <div className="h-7 w-7 rounded-full bg-ink-100 flex items-center justify-center text-2xs font-semibold text-ink-600 shrink-0">
                    {avatarInitials(c.firstName, c.lastName, c.email)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-ink-900 truncate">
                      {c.firstName} {c.lastName}
                    </p>
                    <p className="text-xs text-ink-500 truncate">
                      {c.email}
                      {c.externalReference ? ` · ${c.externalReference}` : ''}
                    </p>
                  </div>
                </button>
              ))
            )}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={() => setManualEntry(true)}
        className="mt-1.5 flex items-center gap-1 text-xs text-ink-400 hover:text-ink-600"
      >
        <User className="h-3 w-3" />
        Enter customer ID manually instead
      </button>
    </div>
  );
};

export default CustomerPicker;
